import type { TFunction } from 'i18next';

import type { Consejo } from './consejos';
import type { Respuesta } from './estadoGavi';

export type TextoConsejo = {
  /** Título de la notificación. */
  titulo: string;
  /** Lo que dice Gavi. */
  texto: string;
  /** Pregunta antes del consejo (con sus botones en la ficha). */
  pregunta?: string;
  /** Guion de la llamada («¿Qué les digo?»), tres frases. */
  guion?: string[];
  /** Fuente, en letra pequeña. */
  fuente?: string;
};

type Formatos = {
  /** Céntimos → «612,40 €». */
  euros: (centimos: number) => string;
  /** `YYYY-MM-DD` → «3 de diciembre». */
  fecha: (fecha: string) => string;
};

function porcentaje(cambio: number | undefined): number {
  return Math.round((cambio ?? 0) * 100);
}

/**
 * Lo que dice Gavi de cada consejo, con los números de la persona.
 * `respuesta` es la de su pregunta, si la tiene.
 */
export function textoConsejo(
  t: TFunction,
  { euros, fecha }: Formatos,
  c: Consejo,
  respuesta?: Respuesta,
): TextoConsejo {
  const d = c.datos;
  const comercio = d.comercio || t('this bill');
  const guionNegociar = [
    t('You have raised my price.'),
    t('I am thinking it over. What offer do you have for existing customers?'),
    t('Please send it to me in writing.'),
  ];
  switch (c.id) {
    case 'baja-vuelve':
      return {
        titulo: t('{{name}} charged you again', { name: comercio }),
        texto: t(
          'You cancelled {{name}}, but on {{date}} they charged you {{amount}}. Ask for a refund in writing and keep proof of the cancellation.',
          {
            name: comercio,
            date: fecha(d.fecha ?? ''),
            amount: euros(d.importe),
          },
        ),
      };
    case 'contrato-promo':
      return {
        titulo: t('Your {{name}} discount ends soon', { name: comercio }),
        texto: t(
          'Your {{name}} discount ends on {{date}}. Call before then, ask what you will pay afterwards and ask for the offer for existing customers in writing.',
          { name: comercio, date: fecha(d.fecha ?? '') },
        ),
        guion: guionNegociar,
      };
    case 'contrato-permanencia':
      return {
        titulo: t('Your commitment with {{name}} ends', { name: comercio }),
        texto: t(
          'Your commitment with {{name}} ends on {{date}}. From then on you can ask for a better price without any penalty: they know you are free to go.',
          { name: comercio, date: fecha(d.fecha ?? '') },
        ),
        guion: guionNegociar,
      };
    case 'seguro-renovacion': {
      const salud = c.tipo === 'seguro-salud';
      const antes =
        d.antes != null && d.ahora != null
          ? ` ${t('Last year it went up by {{amount}}.', {
              amount: euros(d.ahora - d.antes),
            })}`
          : '';
      const anios =
        d.anios != null
          ? ` ${t('You have been with them for {{count}} years.', {
              count: d.anios,
            })}`
          : '';
      return salud
        ? {
            titulo: t('Your {{name}} insurance renews on {{date}}', {
              name: comercio,
              date: fecha(d.fecha ?? ''),
            }),
            texto:
              t(
                'Your {{name}} health insurance renews on {{date}}. If the price goes up, they must tell you two months ahead: if no letter arrives, call and ask for the price for next year.',
                { name: comercio, date: fecha(d.fecha ?? '') },
              ) + anios,
            fuente: t('Insurance Contract Act, article 22'),
          }
        : {
            titulo: t('Your {{name}} insurance renews on {{date}}', {
              name: comercio,
              date: fecha(d.fecha ?? ''),
            }),
            texto:
              t(
                'Your {{name}} insurance ({{amount}} a year) renews on its own on {{date}}. To be able to say no, you must tell them in writing one month before. Now is your moment: call, say you are thinking it over and ask for their offer in writing.',
                {
                  name: comercio,
                  amount: euros(d.anual),
                  date: fecha(d.fecha ?? ''),
                },
              ) +
              antes +
              anios,
            guion: guionNegociar,
            fuente: t('One month notice: Insurance Contract Act, article 22'),
          };
    }
    case 'seguro-subida': {
      const datos = {
        name: comercio,
        before: euros(d.antes ?? 0),
        now: euros(d.ahora ?? 0),
        percent: porcentaje(d.cambio),
      };
      if (c.tipo === 'seguro-salud') {
        return {
          titulo: t('{{name}} raised your health insurance', datos),
          texto: t(
            '{{name}} raises your health insurance from {{before}} to {{now}} (+{{percent}} %). Threatening to leave is not wise here: ask them to lower the rise, for a co-payment or to adjust cover you do not use.',
            datos,
          ),
        };
      }
      if (c.tipo === 'seguro-hogar') {
        return {
          titulo: t('{{name}} raised your home insurance', datos),
          texto: t(
            'Your home insurance goes from {{before}} to {{now}} (+{{percent}} %). Ask {{name}} to adjust building and contents to what your home is worth today: neither paying too much nor falling short.',
            datos,
          ),
          guion: guionNegociar,
          fuente: t('According to OCU'),
        };
      }
      return {
        titulo: t('{{name}} raised your car insurance', datos),
        texto:
          t(
            'Your car insurance went from {{before}} to {{now}} (+{{percent}} %). Without claims, there is no reason: call and ask them to leave it as it was.',
            datos,
          ) +
          (d.anios != null
            ? ` ${t('Tell them you have been with them for {{count}} years.', {
                count: d.anios,
              })}`
            : ''),
        guion: guionNegociar,
        fuente: t(
          'If the letter arrived with less than two months of notice, say so: Insurance Contract Act, article 22',
        ),
      };
    }
    case 'seguro-fraccionado':
      return {
        titulo: t('Your {{name}} insurance in instalments', { name: comercio }),
        texto: t(
          'You pay the insurance in {{count}} instalments: {{amount}} a year. Paying in instalments usually has a surcharge: ask for the price of paying it all at once and compare.',
          { count: d.cuotas ?? 12, amount: euros(d.anual) },
        ),
        fuente: t('According to Acierto, between 4 and 12 %'),
      };
    case 'coche-cobertura':
      return respuesta === 'si'
        ? {
            titulo: t('Car insurance cover'),
            texto: t(
              'With a car that is a few years old, comprehensive cover pays at most what the car is worth today. Ask {{name}} for the price of extended third-party cover or comprehensive with excess, and compare.',
              { name: comercio },
            ),
            fuente: t('According to OCU, from 5 or 6 years'),
          }
        : {
            titulo: t('Car insurance cover'),
            texto: t('Is your car more than 5 years old?'),
            pregunta: t('Is your car more than 5 years old?'),
          };
    case 'consumo-subida': {
      const datos = {
        name: comercio,
        percent: porcentaje(d.cambio),
        amount: euros(Math.abs(d.anual)),
      };
      const luz = c.tipo === 'luz';
      const base = luz
        ? t(
            'Over the last months you pay {{percent}} % more for electricity than in the same months last year: about {{amount}} more a year. You may be using more, or they may have raised the price.',
            datos,
          )
        : t(
            'Over the last months you pay {{percent}} % more for gas than in the same months last year: about {{amount}} more a year. You may be using more, or they may have raised the price.',
            datos,
          );
      if (respuesta === 'si') {
        return {
          titulo: t('Your bill with {{name}}', datos),
          texto: luz
            ? t(
                'Then call {{name}} and say you are thinking it over: when they raise the electricity price they must warn you in writing a month ahead and you can leave at no cost. That is usually enough for them to keep your price.',
                datos,
              )
            : t(
                'Then call {{name}} and say you are thinking it over: they usually improve the price so you stay. Ask for it in writing.',
                datos,
              ),
          guion: guionNegociar,
          fuente: luz ? t('Royal Decree 88/2026') : undefined,
        };
      }
      if (respuesta === 'no') {
        return {
          titulo: t('Your bill with {{name}}', datos),
          texto: t(
            'Then it is what you use, and there is nothing to claim. Gavi will tell you if the price per unit goes up.',
          ),
        };
      }
      if (respuesta === 'nose') {
        return {
          titulo: t('Your bill with {{name}}', datos),
          texto: t(
            'Look at the price per kWh on your last bill and on the one from a year ago. If it is higher, they raised it: call and say you are thinking it over.',
          ),
          guion: guionNegociar,
        };
      }
      return {
        titulo: luz
          ? t('Your electricity, +{{percent}} % on last year', datos)
          : t('Your gas, +{{percent}} % on last year', datos),
        texto: base,
        pregunta: t('Did {{name}} tell you about a price rise?', datos),
      };
    }
    case 'primer-anio':
      return {
        titulo: t('11 months with {{name}}', { name: comercio }),
        texto: t(
          'You have been with {{name}} for 11 months. Many first-year discounts end without warning: call before {{date}}, ask what you will pay and ask for the offer for existing customers in writing.',
          { name: comercio, date: fecha(d.fecha ?? '') },
        ),
        guion: guionNegociar,
        fuente: t('According to OCU'),
      };
    case 'retencion':
      return {
        titulo: t('Two years with {{name}}', { name: comercio }),
        texto: t(
          'You have been paying {{amount}} to {{name}} for 23 months: the usual commitment ends at 24. Good time to call and ask for a cheaper plan from the same company, in writing.',
          { name: comercio, amount: euros(d.importe) },
        ),
        guion: guionNegociar,
      };
    case 'luz-potencia':
      return {
        titulo: t('The power you have contracted'),
        texto: t(
          'Contracted power is paid whether you use it or not: about 28 € a year for each kW. Check in your customer area the maximum power you have used; if you have too much, ask to lower it (it costs about 9 € plus VAT).',
        ),
        fuente: t('According to Selectra, 2026, before taxes'),
      };
    case 'consumo-extra':
      return {
        titulo: t('An extra charge from {{name}}', { name: comercio }),
        texto: t(
          'Besides the bill, {{name}} charges you {{amount}} for a separate service: {{year}} a year. If you did not ask for it or do not use it, ask to cancel it in writing.',
          {
            name: comercio,
            amount: euros(d.extra ?? 0),
            year: euros(d.anual),
          },
        ),
        fuente: t('According to complaints published by OCU'),
      };
    case 'tele-subida': {
      const datos = {
        name: comercio,
        before: euros(d.antes ?? 0),
        now: euros(d.ahora ?? 0),
        diff: euros((d.ahora ?? 0) - (d.antes ?? 0)),
        year: euros(d.anual),
      };
      return {
        titulo: t('{{name}} raised your price by {{diff}}', datos),
        texto: t(
          '{{name}} raised you from {{before}} to {{now}}: {{diff}} more, {{year}} a year. They must warn you a month ahead and you can leave without penalty (check your contract in case the rise was agreed). Call, say so and ask to keep the old price.',
          datos,
        ),
        guion: guionNegociar,
        fuente: t('General Telecommunications Act, article 67'),
      };
    }
    case 'tele-baila':
      return {
        titulo: t('Your {{name}} bill varies', { name: comercio }),
        texto: t(
          'Your {{name}} bill varies: from {{min}} to {{max}} in three months. Look on the bill for premium SMS, third-party services or rented decoders and routers; cancel what you do not use and ask them to block premium numbers.',
          {
            name: comercio,
            min: euros(d.minimo ?? 0),
            max: euros(d.maximo ?? 0),
          },
        ),
        fuente: t('According to OCU'),
      };
    case 'tele-plataforma':
      return {
        titulo: t('{{other}} on top of {{name}}', {
          name: comercio,
          other: d.otro ?? '',
        }),
        texto: t(
          'You pay {{amount}} to {{other}} on top of {{name}}. Some {{name}} plans already include it or give it at a discount: ask whether they can add it to your bill before paying twice.',
          { name: comercio, other: d.otro ?? '', amount: euros(d.extra ?? 0) },
        ),
      };
    case 'susc-renovacion':
      return {
        titulo: t('{{name}} renews on {{date}}', {
          name: comercio,
          date: fecha(d.fecha ?? ''),
        }),
        texto: t(
          'On {{date}} {{name}} will charge you {{amount}} for another year. They must warn you at least 15 days before; if you no longer use it, now is the moment to cancel.',
          {
            name: comercio,
            date: fecha(d.fecha ?? ''),
            amount: euros(d.importe),
          },
        ),
        fuente: t('Customer Service Act 10/2025'),
      };
    case 'susc-subida':
      return {
        titulo: t('{{name}} went up', { name: comercio }),
        texto: t(
          '{{name}} goes from {{before}} to {{now}}. The same service usually has a cheaper plan: with ads, or a family plan to share at home.',
          {
            name: comercio,
            before: euros(d.antes ?? 0),
            now: euros(d.ahora ?? 0),
          },
        ),
      };
    case 'susc-coste-anual':
      return {
        titulo: t('Your subscriptions, per year'),
        texto: t(
          'All your subscriptions together cost {{amount}} a year. If there is one you have not used for months, cancel it now: cancelling must be as easy as signing up.',
          { amount: euros(d.anual) },
        ),
        fuente: t('Customer Service Act 10/2025'),
      };
    case 'tarjeta-aplazada':
      return {
        titulo: t('A fixed instalment every month'),
        texto: t(
          'Every month you pay {{amount}} to {{name}}: it looks like deferred payment, which carries high interest. Ask to switch to "pay at the end of the month" and you will stop paying interest.',
          { name: comercio, amount: euros(d.importe) },
        ),
        fuente: t(
          'According to the Bank of Spain, these cards charge around 18 % on average',
        ),
      };
    case 'cuenta-comision':
      return {
        titulo: t('{{name}} charges you a fee', { name: comercio }),
        texto:
          t(
            '{{name}} charges you {{amount}} in fees ({{year}} a year). If you have your salary or bills paid there, ask them to waive it; your contract says which condition exempts you.',
            {
              name: comercio,
              amount: euros(d.importe),
              year: euros(d.anual),
            },
          ) +
          (d.basica
            ? ` ${t('Your bank must also offer you the basic payment account: at most 3 € a month.')}`
            : ''),
        fuente: d.basica ? t('According to the Bank of Spain') : undefined,
      };
    default:
      return { titulo: '', texto: '' };
  }
}
